import React from 'react';
import { Box } from '@mui/material';

const LiveActivityTab = ({ metrics }) => {
  // Combine all activity types into a single chronological feed
  const allActivities = [
    // User signups
    ...metrics.recentUsers.map(user => ({
      type: 'user_signup',
      user: user.email,
      time: user.createdAt,
      icon: '👤',
      detail: user.isAdmin ? 'Admin account' : user.subscriptionStatus === 'active' ? 'Pro subscriber' : null,
    })),
    // Orphan listings
    ...metrics.activeOrphanListings.map(orphan => ({
      type: 'orphan_listed',
      title: orphan.leagueName,
      detail: orphan.teamName,
      time: orphan.createdAt,
      icon: '🏪',
    })),
    // Manager Intel syncs
    ...metrics.managerIntelProfiles.map(profile => ({
      type: 'sync_completed',
      user: profile.sleeperUsername,
      time: profile.lastRefreshedAt || profile.updatedAt,
      icon: profile.status === 'error' ? '❌' : profile.status === 'in_progress' ? '⏳' : '✅',
      status: profile.status,
    })),
  ]
    .filter(a => a.time) // Remove items without timestamps
    .sort((a, b) => new Date(b.time) - new Date(a.time)) // Sort by newest first
    .slice(0, 50); // Take the 50 most recent

  return (
    <Box>
      <Box
        sx={{
          background: '#1E293B',
          border: '1px solid #334155',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <Box sx={{ fontSize: '24px' }}>📡</Box>
          <Box sx={{ fontSize: '20px', fontWeight: 700, color: '#3B82F6' }}>
            Live Activity Feed
          </Box>
          <Box
            sx={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#10B981',
              animation: 'blink 2s infinite',
              '@keyframes blink': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.3 },
              },
            }}
          />
        </Box>

        <Box sx={{ fontSize: '14px', color: '#94A3B8', marginBottom: '24px' }}>
          Real-time events from your application ({allActivities.length} event{allActivities.length !== 1 ? 's' : ''})
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '600px', overflow: 'auto' }}>
          {allActivities.length > 0 ? (
            allActivities.map((activity, index) => (
              <Box
                key={index}
                sx={{
                  background: '#0F172A',
                  border: '1px solid #334155',
                  borderRadius: '8px',
                  padding: '16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  '&:hover': {
                    borderColor: '#3B82F6',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <Box sx={{ fontSize: '24px' }}>{activity.icon}</Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ fontSize: '14px', fontWeight: 600, color: '#F1F5F9', marginBottom: '4px' }}>
                    {activity.type === 'user_signup' && `New user signed up: ${activity.user}`}
                    {activity.type === 'orphan_listed' && `Team listed: ${activity.title}`}
                    {activity.type === 'sync_completed' && `Manager Intel synced: ${activity.user}`}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: '#64748B' }}>
                    {activity.detail && (
                      <Box component="span" sx={{ color: '#94A3B8', marginRight: '8px' }}>
                        {activity.detail} ·
                      </Box>
                    )}
                    {new Date(activity.time).toLocaleString()}
                  </Box>
                </Box>
              </Box>
            ))
          ) : (
            <Box sx={{ textAlign: 'center', padding: '48px', color: '#64748B' }}>
              No recent activity
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          background: '#1E293B',
          border: '1px solid #334155',
          borderRadius: '12px',
          padding: '24px',
        }}
      >
        <Box sx={{ fontSize: '16px', fontWeight: 700, marginBottom: '16px', color: '#F59E0B' }}>
          ⚡ Coming Soon
        </Box>
        <Box sx={{ fontSize: '14px', color: '#94A3B8' }}>
          - WebSocket integration for real-time updates
          <br />
          - User session tracking
          <br />
          - API request logging
          <br />
          - Slow query detection
          <br />
          - Error rate alerts
        </Box>
      </Box>
    </Box>
  );
};

export default LiveActivityTab;
